import { useCallback, useEffect, useRef, useState } from "react";
import LegalConsentCheckbox from "../components/auth/LegalConsentCheckbox";
import { api } from "../lib/api";
import { resolveLineRegisterCallbackUrl } from "../lib/config";
import {
  clearPendingLegalConsent,
  LEGAL_CONSENT_REQUIRED_MESSAGE,
  readPendingLegalConsent,
  registrationLegalErrorMessage,
  savePendingLegalConsent,
} from "../lib/legalConsent";
import {
  LINE_AUTH_MODE_KEY,
  markLineVersionRegisterEntry,
  markWebAppLineRegisterEntry,
  type LineRegisterEntrySource,
} from "../lib/lineRegisterSource";
import { AppError, logError } from "../lib/errors";

const LINE_AUTHORIZE_ORIGIN = "https://access.line.me/";

const IS_STAGING = process.env.REACT_APP_ENV === "staging";

type LineRegisterStartProps = {
  autoStart?: boolean;
  mode?: "login" | "register";
  entrySource?: LineRegisterEntrySource;
  description?: string;
  startButtonLabel?: string;
  loadingMessage?: string;
};

export default function LineRegisterStart({
  autoStart = false,
  mode = "register",
  entrySource = "webApp",
  description = "LINEアカウントで登録する",
  startButtonLabel = "LINEで登録する",
  loadingMessage = "LINEに接続しています...",
}: LineRegisterStartProps) {
  const consentQuery = new URLSearchParams(window.location.search).get("legalConsent");
  const legalReturnTo = entrySource === "lineVersion" ? "/line" : "/line-register";
  const [hadPendingLegalConsent] = useState(
    () => mode === "register" && readPendingLegalConsent() !== null
  );
  const [loading, setLoading] = useState(false);
  const [legalConsentAccepted, setLegalConsentAccepted] = useState(
    () => mode === "login" || hadPendingLegalConsent
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(
    consentQuery === "required" ? LEGAL_CONSENT_REQUIRED_MESSAGE : null
  );
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const hasAutoStartedRef = useRef(false);

  const handleRegister = useCallback(async () => {
    if (loading) return;
    if (mode === "register" && !legalConsentAccepted) {
      setErrorMessage(LEGAL_CONSENT_REQUIRED_MESSAGE);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    if (mode === "register") {
      savePendingLegalConsent();
    }
    sessionStorage.setItem(LINE_AUTH_MODE_KEY, mode);

    try {
      const data = await api.startLineRegister({
        redirectUri: resolveLineRegisterCallbackUrl(),
        botPrompt: entrySource === "lineVersion" ? "normal" : undefined,
      });

      if (!data.authorizeUrl.startsWith(LINE_AUTHORIZE_ORIGIN)) {
        throw new AppError("Unexpected authorize URL.", "INVALID_RESPONSE");
      }

      if (entrySource === "lineVersion") {
        markLineVersionRegisterEntry();
      } else {
        markWebAppLineRegisterEntry();
      }

      window.location.assign(data.authorizeUrl);
    } catch (e: unknown) {
      if (IS_STAGING) {
        const lines: string[] = [];
        if (e instanceof AppError) {
          lines.push(`code: ${e.code}`);
          lines.push(`message: ${e.message}`);
          if (e.meta) lines.push(`meta: ${JSON.stringify(e.meta, null, 2)}`);
        } else if (e instanceof Error) {
          lines.push(`${e.name}: ${e.message}`);
        } else {
          lines.push(String(e));
        }
        setDebugInfo(lines.join("\n"));
      }

      const legalErrorMessage = registrationLegalErrorMessage(e);
      if (legalErrorMessage) {
        setErrorMessage(legalErrorMessage);
      } else if (e instanceof AppError && e.code === "BAD_REQUEST") {
        let displayMessage = e.message;
        try {
          const parsed = JSON.parse(e.meta.responseText ?? "");
          if (typeof parsed?.message === "string" && parsed.message) {
            displayMessage = parsed.message;
          }
        } catch { /* use default message */ }
        setErrorMessage(displayMessage);
      } else if (e instanceof AppError && e.code === "MAINTENANCE") {
        setErrorMessage("現在サービスに接続できません。時間をおいてお試しください。");
      } else {
        logError(e);
        setErrorMessage("エラーが発生しました。時間をおいて再度お試しください。");
      }
      setLoading(false);
    }
  }, [entrySource, legalConsentAccepted, loading, mode]);

  useEffect(() => {
    if (!autoStart || (mode === "register" && !legalConsentAccepted) || hasAutoStartedRef.current) {
      return;
    }

    hasAutoStartedRef.current = true;
    void handleRegister();
  }, [autoStart, handleRegister, legalConsentAccepted, mode]);

  const isAutoStartPending =
    autoStart &&
    (mode === "login" || legalConsentAccepted) &&
    !hasAutoStartedRef.current &&
    !errorMessage;
  const showLoading = loading || isAutoStartPending;

  return (
    <main className="flex min-h-screen select-none items-center justify-center bg-[#FDF8F5] px-6 text-[#4A3E39] antialiased">
      <div className="w-full max-w-sm">
        <div className="relative overflow-hidden rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_14px_28px_rgba(166,124,82,0.16)] backdrop-blur-sm">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-[#F4E3D7] opacity-70 blur-3xl" />

          <div className="px-8 pb-10 pt-12 text-center">
            <p className="text-[11px] font-bold tracking-[0.12em] text-[#A67C52]">PERSONAL CONCIERGE</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-[#7C5A4E]">WizMate</h1>

            <div className="mx-auto mb-6 mt-6 h-40 w-40 overflow-hidden rounded-full border-[6px] border-white bg-white shadow-[0_10px_25px_rgba(166,124,82,0.2)]">
              <img src="/img/logo.jpg" alt="WizMate Logo" className="h-full w-full object-cover object-center" />
            </div>

            <p className="text-sm font-medium text-[#7B6861]">{description}</p>

            <div className="mt-8 space-y-4">
              {errorMessage ? (
                <p className="rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-xs font-medium text-[#A1493E]">
                  {errorMessage}
                </p>
              ) : null}

              {mode === "register" && !hadPendingLegalConsent ? (
                <LegalConsentCheckbox
                  id={`line-register-legal-consent-${entrySource}`}
                  checked={legalConsentAccepted}
                  returnTo={legalReturnTo}
                  disabled={loading}
                  onChange={(checked) => {
                    setLegalConsentAccepted(checked);
                    setErrorMessage(null);
                    if (checked) {
                      savePendingLegalConsent();
                    } else {
                      clearPendingLegalConsent();
                    }
                  }}
                />
              ) : null}

              {showLoading ? (
                <div className="rounded-2xl border border-[#EADBC8] bg-white/70 px-4 py-4">
                  <p className="text-sm font-bold text-[#5D4D46]">{loadingMessage}</p>
                  <div className="mt-4 flex items-center justify-center gap-1.5" aria-label="loading">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.2s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.1s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F]" />
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={mode === "register" && !legalConsentAccepted}
                  onClick={() => { void handleRegister(); }}
                  className="w-full rounded-2xl bg-[#06C755] px-4 py-4 text-sm font-bold text-white shadow-[0_4px_12px_rgba(6,199,85,0.3)] transition hover:bg-[#05b34d] active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-[#B8C8BE] disabled:shadow-none"
                >
                  {startButtonLabel}
                </button>
              )}
            </div>
          </div>

          {IS_STAGING && debugInfo ? (
            <div className="border-t border-[#EADBC8]">
              <button
                type="button"
                onClick={() => setDebugOpen((o) => !o)}
                className="w-full px-6 py-3 text-left text-[11px] font-bold text-[#A67C52]"
              >
                [STAGING] debug {debugOpen ? "▲" : "▼"}
              </button>
              {debugOpen ? (
                <pre className="overflow-x-auto whitespace-pre-wrap break-all bg-[#F7F0EA] px-6 pb-4 text-[11px] text-[#4A3E39]">
                  {debugInfo}
                </pre>
              ) : null}
            </div>
          ) : null}

          <div className="border-t border-[#EADBC8] px-6 py-4 text-center">
            <p className="text-[11px] text-[#9C867D]">© WizMate</p>
          </div>
        </div>
      </div>
    </main>
  );
}
